import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAtomValue } from 'jotai';
import { FitToScreen } from '@carbon/react/icons';
import type { MultiPolygon, Polygon, Position } from 'geojson';
import { Map, useMapControl } from 'components/map';
import { MapPickControl, type SurveyAreaCandidate } from 'components/query-map';
import {
  SURVEY_FILL_LAYER,
  SurveyAreasLayer,
  SNAP_LAYER_IDS,
  SNAP_LAYER_PREFIXES,
  snapPositionToSurveyGrid,
} from 'components/query-map';
import { DrawCircleControl } from 'components/create-area-panel';
import { createEditorMapConfigAtom, editorMapRefAtom } from './atoms';
import { DrawPolygonGesture } from './draw-polygon-gesture';
import { EditedAreaLayer } from './edited-area-layer';
import type {
  Bounds,
  EditorTool,
  Pieces,
  ReferenceArea,
  TraceSource,
} from './types';
import { pieceToPolygon, piecesBounds, polygonToPiece } from './utils';
import { VertexEditLayer } from './vertex-edit-layer';
import {
  REFERENCE_FILL_LAYER,
  ReferenceAreasLayer,
  ReferenceHighlightLayer,
} from './reference-areas-layer';
import { TraceGesture } from './trace-gesture';
import { AreaNameTooltip } from './area-name-tooltip';
import styles from './area-geometry-editor.module.scss';

/** Stable default for the picked survey areas. */
const NO_IDS: number[] = [];

/** Stable default for the reference areas. */
const NO_AREAS: ReferenceArea[] = [];

/** Stable default for the county and state lines. */
const NO_TRACE_SOURCES: TraceSource[] = [];

/** Stable default for the outlined reference areas. */
const NO_HIGHLIGHTS: (Polygon | MultiPolygon)[] = [];

/** The snap layers that are the land grid, taken only while its toggle is on. */
const LAND_GRID_SNAP_LAYERS = [SURVEY_FILL_LAYER, 'Section', 'Texas Sections'];

/** Room around the area when the map fits to it, as the mock leaves. */
const FIT_PADDING = 40;

interface EditorMapProps {
  /** Every part, drawn as the edited area. */
  parts: Pieces;
  /** The shape the parts left as they were take their holes from. */
  holesFrom?: Polygon | MultiPolygon | null;
  /** The edited area's name, for its hover tooltip. */
  areaName: string;
  /** The drawn parts, which the tools edit. */
  drawn: Pieces;
  color: string;
  tool: EditorTool;
  /** False in Area from Survey and while saving: the tools stand down. */
  toolsEnabled: boolean;
  onDrawnChange: (pieces: Pieces) => void;
  /** A finished circle or polygon: the drawn parts, to merge into the rest. */
  onShapeCommit: (pieces: Pieces) => void;
  onBeforeChange: () => void;
  /** Set while survey rows are on screen to take a map pick. */
  onSurveyAreaPick?: ((candidates: SurveyAreaCandidate[]) => void) | null;
  /** Survey areas already in those rows. */
  pickedSurveyIds?: number[];
  /** Other areas shown for reference, snapped to and traced along. */
  referenceAreas?: ReferenceArea[];
  /** The county and state lines in view, and any land grid, traced along too. */
  traceBoundaries?: TraceSource[];
  /** Whether Snap takes the land grid: the survey sections and the base map's
   * section lines. Trace gets its sections through traceBoundaries. */
  landGrid?: boolean;
  /** Snap: dragged, added and placed vertices pull onto nearby boundaries. */
  snap?: boolean;
  /** The reference rows put on the map with Show on map, outlined in orange. */
  highlightGeometries?: (Polygon | MultiPolygon)[];
  /** Reports the map's extent each time it stops moving. */
  onViewChange?: (bounds: Bounds) => void;
}

/**
 * Zoom to area, in the map's own control stack beside zoom and Layers.
 *
 * @param {{ parts: Pieces }} props - The parts to fit.
 * @return {null} The control renders in the stack.
 */
function ZoomToAreaControl({ parts }: { parts: Pieces }): null {
  const mapRef = useAtomValue(editorMapRefAtom);
  useMapControl({
    id: 'area-edit-zoom-to-area',
    order: -1,
    render: ({ className }) => (
      <button
        type="button"
        className={className}
        title="Zoom to area"
        aria-label="Zoom to area"
        onClick={() => {
          const bounds = piecesBounds(parts);
          if (bounds && mapRef)
            mapRef.fitBounds(bounds, { padding: FIT_PADDING });
        }}
      >
        <FitToScreen size={16} />
      </button>
    ),
  });
  return null;
}

/**
 * The editor's map: the area in its colour, the tool in use, survey picking in
 * Area from Survey, and Zoom to area in the control stack. The shared map brings
 * zoom, Layers and Satellite with it; Layers lists only its general layers, the
 * edited and reference areas staying on.
 *
 * @param {EditorMapProps} props - The parts, the tool and their callbacks.
 * @return {JSX.Element} The map.
 */
export function EditorMap({
  parts,
  holesFrom = null,
  areaName,
  drawn,
  color,
  tool,
  toolsEnabled,
  onDrawnChange,
  onShapeCommit,
  onBeforeChange,
  onSurveyAreaPick,
  pickedSurveyIds = NO_IDS,
  referenceAreas = NO_AREAS,
  traceBoundaries = NO_TRACE_SOURCES,
  landGrid = false,
  snap = false,
  highlightGeometries = NO_HIGHLIGHTS,
  onViewChange,
}: EditorMapProps): JSX.Element {
  const mapRef = useAtomValue(editorMapRefAtom);
  // Made once per mount, and the editor mounts its map afresh on each opening.
  const [mapConfigAtom] = useState(createEditorMapConfigAtom);
  const initial = useMemo(() => {
    const bounds = piecesBounds(parts);
    return bounds
      ? {
          longitude: (bounds[0] + bounds[2]) / 2,
          latitude: (bounds[1] + bounds[3]) / 2,
        }
      : { longitude: -98.58, latitude: 39.83 };
    // Only the opening view: the map then follows the user.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- the parts change with every edit, and refitting would yank the view mid-gesture.
  }, []);

  // Open on the whole area.
  useEffect(() => {
    const bounds = piecesBounds(parts);
    if (mapRef && bounds) {
      mapRef.fitBounds(bounds, { padding: FIT_PADDING, duration: 0 });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- once, when the map arrives; later edits keep the user's view.
  }, [mapRef]);

  // The Scoped areas list follows the view: read it on arrival and per move.
  useEffect(() => {
    if (!mapRef || !onViewChange) return undefined;
    /** Hands the map's current extent up. */
    const report = () => {
      const bounds = mapRef.getBounds();
      if (bounds) {
        onViewChange([
          bounds.getWest(),
          bounds.getSouth(),
          bounds.getEast(),
          bounds.getNorth(),
        ]);
      }
    };
    report();
    mapRef.on('moveend', report);
    return () => {
      mapRef.off('moveend', report);
    };
  }, [mapRef, onViewChange]);

  const drawnPolygons = useMemo(() => drawn.map(pieceToPolygon), [drawn]);
  const handleCircles = useCallback(
    (polygons: Polygon[]) => {
      onBeforeChange();
      onShapeCommit(polygons.map(polygonToPiece));
    },
    [onBeforeChange, onShapeCommit]
  );
  const handlePolygon = useCallback(
    (ring: Position[]) => {
      onBeforeChange();
      onShapeCommit([...drawn, ring]);
    },
    [drawn, onBeforeChange, onShapeCommit]
  );

  const drawing = toolsEnabled && (tool === 'polygon' || tool === 'circle');
  const showReferences = referenceAreas.length > 0;
  // Trace follows the shown areas and the county and state lines in view.
  const traceSources = useMemo(
    () => [
      ...(showReferences
        ? referenceAreas.map((area) => ({
            key: `area-${area.id}`,
            geometry: area.geometry,
          }))
        : []),
      ...traceBoundaries,
    ],
    [showReferences, referenceAreas, traceBoundaries]
  );

  // Snap pulls onto units and any reference boundary on screen, and onto the
  // survey grid and section lines while the land grid is on, whichever is nearer:
  // the catalog's snapper, with the references added.
  const snapPosition = useMemo(() => {
    if (!snap || !mapRef) return undefined;
    const layerIds = [
      ...SNAP_LAYER_IDS.filter(
        (id) => landGrid || !LAND_GRID_SNAP_LAYERS.includes(id)
      ),
      REFERENCE_FILL_LAYER,
    ];
    return (position: Position) =>
      snapPositionToSurveyGrid(mapRef, position, {
        layerIds,
        layerPrefixes: SNAP_LAYER_PREFIXES,
      });
  }, [snap, landGrid, mapRef]);

  return (
    <Map
      className={styles.mapCanvas}
      longitude={initial.longitude}
      latitude={initial.latitude}
      zoom={10}
      mapConfigAtom={mapConfigAtom}
      refAtom={editorMapRefAtom}
      showControls
      showMapFilters
      hideMeasureAndPricing
      // Every tool is a press on the map, and a hover popup left over a vertex
      // would swallow it; the popups stay for the survey method.
      blockPopups={toolsEnabled}
    >
      {/* Mounted for picking, and for snapping to the land grid, which hit-tests it. */}
      {(!!onSurveyAreaPick || (snap && landGrid)) && (
        <SurveyAreasLayer
          pickedIds={pickedSurveyIds}
          showFill={!!onSurveyAreaPick}
        />
      )}
      {onSurveyAreaPick && (
        <MapPickControl onPickSurveyArea={onSurveyAreaPick} />
      )}
      {showReferences && (
        <ReferenceAreasLayer
          areas={referenceAreas}
          tracing={toolsEnabled && tool === 'trace'}
        />
      )}
      {highlightGeometries.length > 0 && (
        <ReferenceHighlightLayer geometries={highlightGeometries} />
      )}
      <EditedAreaLayer pieces={parts} holesFrom={holesFrom} color={color} />
      <VertexEditLayer
        pieces={drawn}
        color={color}
        // Hidden while a circle or polygon is being drawn, as the mock has it.
        visible={!drawing}
        active={toolsEnabled && tool === 'move'}
        onChange={onDrawnChange}
        onBeforeChange={onBeforeChange}
        snapPosition={snapPosition}
      />
      {toolsEnabled && tool === 'polygon' && (
        <DrawPolygonGesture
          color={color}
          onCommit={handlePolygon}
          snapPosition={snapPosition}
        />
      )}
      {toolsEnabled && tool === 'trace' && (
        <TraceGesture
          pieces={drawn}
          references={traceSources}
          onChange={onDrawnChange}
          onBeforeChange={onBeforeChange}
        />
      )}
      {toolsEnabled && tool === 'circle' && (
        <DrawCircleControl
          look="editor"
          color={color}
          polygons={drawnPolygons}
          onPolygonsChange={handleCircles}
        />
      )}
      <ZoomToAreaControl parts={parts} />
      <AreaNameTooltip editedName={areaName} />
    </Map>
  );
}
